import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import api from 'config/axiosConfig';
import InputField from 'ui/InputField';
import Modal from 'ui/Modal';

const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    maxLength: 100
};

const PasswordResetPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/');
        }
    }, [token, navigate]);

    const validatePassword = (password: string): string | null => {
        if (password.length < PASSWORD_REQUIREMENTS.minLength) {
            return 'Password must be at least 8 characters long';
        }
        if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
            return 'Password must not exceed 100 characters';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate passwords
        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await api.post('/auth/password/reset', {
                token,
                newPassword
            });

            if (response.status === 200) {
                setIsSuccess(true);
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to reset password');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return null;
    }

    return (
        <Modal
            isOpen={true}
            onClose={() => navigate('/login')}
            title="Reset your password"
        >
            <div className="flex flex-col flex-grow h-full justify-between mb-4 space-y-4">
                {isSuccess ? (
                    <div className="flex flex-col flex-grow justify-between mb-4 space-y-4">
                        <p className="text-green-600 dark:text-green-400">
                            Your password has been reset successfully!
                        </p>

                        <p className="text-eoe-text-meta dark:text-eoe-dark-text-meta">
                            Redirecting you to login...
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto">
                        <div className="flex flex-col flex-grow justify-between space-y-4 mb-4">
                        <p className="text-left text-sm text-eoe-text-meta dark:text-eoe-dark-text-meta">
                            Set a new password.
                        </p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <InputField
                                type="password"

                                id="newPassword"
                                name="newPassword"
                                labelText="New password"
                                autoComplete="new-password"
                                fieldRequired
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                validationState={newPassword.length >= PASSWORD_REQUIREMENTS.minLength ? 'valid' : newPassword ? 'invalid' : 'none'}
                                spellCheck={false}
                                maxLength={PASSWORD_REQUIREMENTS.maxLength}
                                minLength={PASSWORD_REQUIREMENTS.minLength}
                            />

                            <InputField
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                labelText="Confirm password"
                                autoComplete="new-password"
                                fieldRequired
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                validationState={confirmPassword === newPassword && confirmPassword.length >= PASSWORD_REQUIREMENTS.minLength ? 'valid' : confirmPassword ? 'invalid' : 'none'}
                                spellCheck={false}
                            />

                            {error && (
                                <div className="flex items-center text-red-500 text-sm">
                                    <Info className="mr-4" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </form>

                        <div className="flex flex-grow flex-col pb-4 justify-end place-items-end mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:pb-2">
                            <button
                                type="button"
                                onClick={() => navigate('/')}
                                className="btn-secondary w-full sm:w-fit py-2 px-4"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < PASSWORD_REQUIREMENTS.minLength}
                                className={`w-full sm:w-fit py-2 px-4 ${
                                    isSubmitting || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < PASSWORD_REQUIREMENTS.minLength
                                        ? 'btn-primary-disabled'
                                        : 'btn-primary'
                                }`}

                            >
                                {isSubmitting ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>

    );
};

export default PasswordResetPage; 